import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Tooltip,
  Badge,
} from "@mui/material";
import CustomButton from "./CustomButton";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { useTranslation } from "react-i18next";

export interface ColumnDefinition<T> {
  id: keyof T | string;
  label: string;
  sortable?: boolean;
}

interface TableColumnSelectorProps<T> {
  columns: ColumnDefinition<T>[];
  visibleColumns: (keyof T | string)[];
  onChange: (newVisible: (keyof T | string)[]) => void;
}

export function TableColumnSelector<T>({
  columns,
  visibleColumns,
  onChange,
}: TableColumnSelectorProps<T>) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (columnId: keyof T | string) => {
    // Prevent unchecking the last visible column
    if (visibleColumns.length === 1 && visibleColumns.includes(columnId)) {
      return;
    }

    const currentIndex = visibleColumns.indexOf(columnId);
    const newChecked = [...visibleColumns];

    if (currentIndex === -1) {
      newChecked.push(columnId);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    onChange(newChecked);
  };

  return (
    <>
      <Tooltip title={t("common.columns", "Configurar Colunas")}>
        <CustomButton
          variant="icon"
          onClick={handleClick}
          icon={
            <Badge
              badgeContent={
                columns.filter((c) => !visibleColumns.includes(c.id)).length
              }
              color="primary"
            >
              <ViewColumnIcon />
            </Badge>
          }
          iconPosition="only"
          customSize={{ width: 36, height: 36 }}
        />
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: "25ch",
          },
        }}
      >
        {columns.map((col) => {
          const label = col.label || String(col.id);
          return (
            <MenuItem
              key={String(col.id)}
              onClick={() => handleToggle(col.id)}
              dense
            >
              <Checkbox
                edge="start"
                checked={visibleColumns.indexOf(col.id) !== -1}
                tabIndex={-1}
                disableRipple
                size="small"
              />
              <ListItemText primary={label} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
